import { useState } from 'react'
import { X, Plus } from 'lucide-react'
import type { CustomerCard } from '@/types/dashboard'
import { useCustomerTrackerStore } from '@/stores/customer-tracker-store'
import { format } from 'date-fns'

interface CustomerTimelineProps {
  customer: CustomerCard
  onClose: () => void
}

export function CustomerTimeline({ customer, onClose }: CustomerTimelineProps) {
  const { updateDescription, addTimelineEntry, removeTimelineEntry } = useCustomerTrackerStore()
  const [isAddingEntry, setIsAddingEntry] = useState(false)
  const [newEntryDate, setNewEntryDate] = useState('')
  const [newEntryNote, setNewEntryNote] = useState('')
  const [newEntryType, setNewEntryType] = useState<'past' | 'future'>('past')
  const [editingDescription, setEditingDescription] = useState(false)
  const [tempDescription, setTempDescription] = useState(customer.currentDescription || '')

  const handleAddEntry = (e: React.FormEvent) => {
    e.preventDefault()
    if (newEntryDate && newEntryNote.trim()) {
      addTimelineEntry(customer.id, newEntryDate, newEntryNote.trim(), newEntryType)
      setNewEntryDate('')
      setNewEntryNote('')
      setIsAddingEntry(false)
    }
  }

  const handleSaveDescription = () => {
    updateDescription(customer.id, tempDescription)
    setEditingDescription(false)
  }

  const today = new Date().toISOString().split('T')[0]

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1a1b1d] border border-white/[0.06] rounded-lg w-[600px] max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">{customer.name}</h2>
          <button onClick={onClose} className="text-white/70/60 hover:text-white transition-colors" aria-label="Close timeline">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Current Description */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-white">Info</h3>
              {!editingDescription && (
                <button onClick={() => setEditingDescription(true)} className="text-xs text-primary hover:opacity-80 transition-opacity">
                  Edit
                </button>
              )}
            </div>
            {editingDescription ? (
              <div className="space-y-2">
                <textarea
                  value={tempDescription}
                  onChange={(e) => setTempDescription(e.target.value)}
                  placeholder="Describe the current state..."
                  rows={3}
                  className="w-full bg-white/[0.02] text-white px-3 py-2 rounded border border-white/[0.06] focus:border-primary focus:outline-none resize-none text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button onClick={handleSaveDescription} className="bg-primary text-black px-3 py-1.5 rounded hover:opacity-90 transition-opacity text-xs font-medium">
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setTempDescription(customer.currentDescription || '')
                      setEditingDescription(false)
                    }}
                    className="bg-white/[0.02] border border-white/[0.06] text-white px-3 py-1.5 rounded hover:bg-white/[0.03] transition-colors text-xs font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-white/70/80">{customer.currentDescription || 'No description yet'}</p>
            )}
          </div>

          {/* Timeline */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-white">Timeline</h3>
              {!isAddingEntry && (
                <button onClick={() => setIsAddingEntry(true)} className="flex items-center gap-1.5 text-xs text-primary hover:opacity-80 transition-opacity">
                  <Plus className="w-3.5 h-3.5" />
                  Add Entry
                </button>
              )}
            </div>

            {/* Add Entry Form */}
            {isAddingEntry && (
              <div className="mb-4 p-3 bg-white/[0.02] border border-white/[0.06] rounded">
                <form onSubmit={handleAddEntry} className="space-y-2.5">
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={newEntryDate}
                      onChange={(e) => setNewEntryDate(e.target.value)}
                      className="flex-1 bg-white/[0.02] text-white px-3 py-2 rounded border border-white/[0.06] focus:border-primary focus:outline-none text-sm"
                      required
                    />
                    <select value={newEntryType} onChange={(e) => setNewEntryType(e.target.value as 'past' | 'future')} className="bg-white/[0.02] text-white px-3 py-2 rounded border border-white/[0.06] focus:border-primary focus:outline-none text-xs">
                      <option value="past">Past Event</option>
                      <option value="future">Follow-up</option>
                    </select>
                  </div>
                  <textarea
                    value={newEntryNote}
                    onChange={(e) => setNewEntryNote(e.target.value)}
                    placeholder="What happened or what needs to happen?"
                    rows={2}
                    className="w-full bg-white/[0.02] text-white px-3 py-2 rounded border border-white/[0.06] focus:border-primary focus:outline-none resize-none text-sm"
                    required
                  />
                  <div className="flex gap-2">
                    <button type="submit" className="bg-primary text-black px-3 py-1.5 rounded hover:opacity-90 transition-opacity text-xs font-medium flex-1">
                      Add Entry
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingEntry(false)
                        setNewEntryDate('')
                        setNewEntryNote('')
                      }}
                      className="bg-white/[0.02] border border-white/[0.06] text-white px-3 py-1.5 rounded hover:bg-white/[0.03] transition-colors text-xs font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Timeline Entries */}
            {!customer.timeline || customer.timeline.length === 0 ? (
              <p className="text-sm text-white/70/60 text-center py-8">No timeline entries yet</p>
            ) : (
              <div className="space-y-0">
                {customer.timeline.map((entry, index) => {
                  const isPast = new Date(entry.date) <= new Date(today)
                  const entryDate = new Date(entry.date)
                  const isToday = entry.date === today

                  return (
                    <div key={entry.id} className="flex gap-3 group">
                      {/* Timeline Line & Dot */}
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${
                            isToday ? 'bg-primary ring-4 ring-primary/20' : isPast ? 'bg-muted/40' : 'bg-[#3b82f6] ring-2 ring-[#3b82f6]/20'
                          }`}
                        />
                        {index < customer.timeline.length - 1 && <div className={`w-px flex-1 mt-1 mb-1 ${isPast ? 'bg-white/[0.06]' : 'bg-[#3b82f6]/20 border-l border-dashed border-[#3b82f6]/40'}`} />}
                      </div>

                      {/* Entry Content */}
                      <div className="flex-1 pb-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium ${isToday ? 'text-primary' : isPast ? 'text-white/70/70' : 'text-[#3b82f6]'}`}>{format(entryDate, 'MMM d, yyyy')}</span>
                            {!isPast && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#3b82f6]/10 text-[#3b82f6] font-medium">Follow-up</span>}
                          </div>
                          <button onClick={() => removeTimelineEntry(customer.id, entry.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-white/70/60 hover:text-white" aria-label="Remove entry">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-sm text-white/90 leading-relaxed">{entry.note}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
